import { useState, useCallback, useEffect } from 'react';
import { useRegisterPushSubscription, useUnregisterPushSubscription } from './useBackend';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const detectIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

// True when running as an installed PWA (home screen icon on iOS, or installed on desktop)
const detectStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

/**
 * Custom hook for managing browser push notifications via the Web Push API.
 *
 * iOS notes:
 *  - PushManager is only available in standalone mode (home screen PWA) on iOS 16.4+.
 *  - In regular Safari/Chrome, isIOSBrowser is true → show "Add to Home Screen" prompt.
 *  - missingConfig is true when the browser supports push but REACT_APP_VAPID_PUBLIC_KEY
 *    is not set in the build (common cause of "Not Supported" on a capable device).
 */
export const usePushNotifications = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isIOS = detectIOS();
  const isStandalone = detectStandalone();
  // iOS user in regular Safari/Chrome — PushManager unavailable until PWA is installed
  const isIOSBrowser = isIOS && !isStandalone;
  // Permission was explicitly denied
  const isDenied = permission === 'denied';

  // Separate browser capability from app configuration so we can show the right message
  const browserSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  const missingConfig = browserSupported && !VAPID_PUBLIC_KEY;
  const isSupported = browserSupported && !!VAPID_PUBLIC_KEY;

  if (missingConfig) {
    console.warn('[usePushNotifications] REACT_APP_VAPID_PUBLIC_KEY is not set. Push notifications will not work.');
  }

  const registerPushSubscription = useRegisterPushSubscription();
  const unregisterPushSubscription = useUnregisterPushSubscription();

  // Check if user is already subscribed
  useEffect(() => {
    if (isSupported && permission === 'granted') {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          setIsSubscribed(!!subscription);
        });
      });
    }
  }, [isSupported, permission]);

  const requestPermissionAndSubscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser. Try Chrome, Edge, or install the app to your home screen.');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult === 'denied') {
        setError('Permission denied. Please allow notifications in your browser settings and try again.');
        return false;
      }

      if (permissionResult !== 'granted') {
        setError('Notification permission was not granted. Please try again.');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;

      // Reuse an existing browser subscription when one is already active.
      // This avoids creating a second DB entry for the same browser and ensures
      // the endpoint is always associated with the currently logged-in account
      // (the backend UPSERT re-keys any existing row to this user_id).
      const existingSubscription = await registration.pushManager.getSubscription();

      let subscription;
      if (existingSubscription) {
        subscription = existingSubscription;
      } else {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: VAPID_PUBLIC_KEY
        });
      }

      const subscriptionJson = subscription.toJSON();

      await registerPushSubscription.registerPushSubscription({
        endpoint: subscriptionJson.endpoint,
        keys: {
          auth: subscriptionJson.keys.auth,
          p256dh: subscriptionJson.keys.p256dh
        }
      });

      setIsSubscribed(true);
      return true;

    } catch (err) {
      console.error('Failed to subscribe to push notifications:', err);
      setError('Failed to enable notifications. Please try again or use a different browser.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, registerPushSubscription]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const subscriptionJson = subscription.toJSON();

        // Unregister from backend
        await unregisterPushSubscription.unregisterPushSubscription({
          endpoint: subscriptionJson.endpoint
        });

        // Unsubscribe from push manager
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      console.log('Successfully unsubscribed from push notifications');
      return true;

    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, unregisterPushSubscription]);

  /**
   * Attach this browser's existing push subscription to the logged-in user.
   * Does not request permission and does not remove other devices.
   * Desktop and mobile each keep their own endpoint for the same account.
   */
  const syncExistingSubscription = useCallback(async () => {
    if (!isSupported || typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      if (!existingSubscription) return false;

      const subscriptionJson = existingSubscription.toJSON();
      await registerPushSubscription.registerPushSubscription({
        endpoint: subscriptionJson.endpoint,
        keys: {
          auth: subscriptionJson.keys.auth,
          p256dh: subscriptionJson.keys.p256dh,
        },
      });
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.warn('[usePushNotifications] Could not sync this device subscription:', err);
      return false;
    }
  }, [isSupported, registerPushSubscription]);

  return {
    isSupported,
    browserSupported,
    missingConfig,
    isSubscribed,
    loading,
    error,
    permission,
    isIOS,
    isStandalone,
    isIOSBrowser,
    isDenied,
    canRequestPermission: permission === 'default',
    requestPermissionAndSubscribe,
    syncExistingSubscription,
    unsubscribe,
  };
};
